import { Link } from "wouter";
import { SEOHead } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import { ChevronRight, ArrowRight, Zap, Shield, PoundSterling, Globe } from "lucide-react";

const PAGE_URL = "https://thepropertool.com/about";

const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "The Proper Tool",
  "url": "https://thepropertool.com",
  "logo": "https://thepropertool.com/logo.png",
  "description": "AI-powered quoting and estimating software built for UK builders. Upload drawings, photos or a description and get a fully-priced professional quote in under 10 minutes.",
  "foundingDate": "2025",
  "areaServed": ["GB"],
  "knowsAbout": [
    "Construction estimating",
    "AI quoting software",
    "UK builder quoting",
    "Quantity surveying",
  ],
};

const ABOUT_PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About The Proper Tool | Built for UK Builders",
  "url": PAGE_URL,
  "description": "The Proper Tool is AI quoting software built for UK builders by people who know construction. Quote from drawings, photos or a description in minutes.",
  "mainEntity": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com",
  },
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Who built The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool was built by a team with extensive UK construction industry experience. The platform was designed specifically for UK residential builders, with a 519-item UK cost library, VAT and CIS handling, and AI that understands UK construction scopes of work.",
      },
    },
    {
      "@type": "Question",
      "name": "When was The Proper Tool founded?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool launched in 2025 as an AI-native quoting platform built from the ground up for UK builders. It is not a US platform adapted for the UK market.",
      },
    },
    {
      "@type": "Question",
      "name": "What makes The Proper Tool different from other estimating software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Three things set it apart: it is UK-native (not adapted from a US or Australian platform), it uses AI to generate quotes from drawings, photos, or plain-text descriptions rather than requiring manual item entry, and it learns your rates over time through the Pricing Accuracy Index. Most builders produce their first quote in under 10 minutes with no setup required.",
      },
    },
    {
      "@type": "Question",
      "name": "Does The Proper Tool work for sole traders as well as larger builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool is designed for the full spectrum of UK residential builders, from sole traders quoting kitchen extensions to multi-trade contractors pricing new builds. Plans start from £29 per month with a 14-day free trial and no credit card required.",
      },
    },
    {
      "@type": "Question",
      "name": "What type of construction work is The Proper Tool designed for?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool is built for UK residential construction: extensions, loft conversions, new builds, renovations, structural alterations, and fit-outs. Its 519-item UK cost library covers 31 trade categories from groundworks and structural steel to plastering and decoration.",
      },
    },
  ],
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "About", "item": PAGE_URL },
  ],
};

const STATS = [
  { value: "£10M+", label: "quoted value processed" },
  { value: "37%", label: "higher win rate reported by users" },
  { value: "8 min", label: "average time to produce a quote" },
  { value: "2025", label: "founded, AI-native from day one" },
];

const VALUES = [
  {
    icon: Zap,
    title: "Built for speed",
    description:
      "Every decision reduces the time between picking up a job and getting a quote in a client's hands. Most builders produce their first quote in under 10 minutes.",
  },
  {
    icon: Shield,
    title: "UK-specific, not adapted",
    description:
      "Our cost library, VAT handling, CIS awareness, and trade categories are built from the ground up for UK residential construction. Not ported from a US or Australian platform.",
  },
  {
    icon: PoundSterling,
    title: "Accurate pricing matters",
    description:
      "Underpricing loses money. Overpricing loses jobs. The Pricing Accuracy Index learns your historical rates so quotes get closer to your numbers with every job.",
  },
  {
    icon: Globe,
    title: "AI that earns trust",
    description:
      "Every AI-generated quote is fully editable, line by line, and validated before it leaves the tool. The AI assists. The builder decides.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Upload drawings, photos, or a description",
    description:
      "Got structural drawings? Upload the PDF. On site? Take photos. Back in the office? Type or paste your site notes. The AI works from whatever you have.",
    href: "/quote-from-drawings/",
    cta: "Quote from drawings",
  },
  {
    step: "02",
    title: "AI builds the scope of works",
    description:
      "The engine extracts dimensions, identifies the trades involved, and produces a fully-itemised scope against the UK cost library. No manual line-by-line entry.",
    href: "/ai-quoting-software/",
    cta: "How the AI works",
  },
  {
    step: "03",
    title: "Review, adjust, and send",
    description:
      "Every line is editable. Swap rates, add items, apply your margin, and send a branded PDF quote directly to the client from inside the tool.",
    href: "/pricing/",
    cta: "See plans and pricing",
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="About The Proper Tool | Built for UK Builders"
        description="The Proper Tool is AI quoting software built for UK builders by people who know construction. Quote from drawings, photos or a description in minutes."
        canonical="/about"
        schema={[ORGANIZATION_SCHEMA, ABOUT_PAGE_SCHEMA, FAQ_SCHEMA, BREADCRUMB_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">
                Home
              </Link>
              <ChevronRight className="h-4 w-4" />
              <span>About</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Built for UK builders. By people who know construction.
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              The Proper Tool is AI quoting software built from the ground up for UK residential construction. Not adapted from a US platform. Not a generic AI tool. Purpose-built for the way UK builders actually work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link href="/free-trial">
                  Start your free trial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/ai-quoting-software/">See how it works</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Stats bar */}
        <section className="py-10 border-t border-border/40 border-b">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
              {STATS.map((stat) => (
                <div key={stat.label}>
                  <div className="text-3xl sm:text-4xl font-bold text-primary mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What we built and why */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8">What we built and why</h2>
            <div className="space-y-5 text-lg text-muted-foreground leading-relaxed">
              <p>
                The Proper Tool started from a frustration every UK builder knows: pricing a job takes hours. A site visit, a rough sketch, an afternoon on a spreadsheet, and even then you are not confident the numbers are right. Miss something, and you are working at a loss. Price too high, and you do not win the job.
              </p>
              <p>
                Existing software was either too expensive and complex for small builders, or US-built tools with pricing databases calibrated for a completely different market. UK labour rates, VAT, CIS, NRM2 trade categories, padstones and structural steel costs for a Birmingham extension are not what American estimating tools are built around.
              </p>
              <p>
                We built The Proper Tool to fix that: a 519-item UK construction cost library, AI that reads structural drawings and extracts real dimensions, and a Pricing Accuracy Index that learns your historical rates so it gets closer to your numbers every job. From a sole trader quoting a loft conversion to a multi-trade contractor pricing a full new build, the goal is the same: get a professional, accurate quote to the client faster.
              </p>
              <p>
                The platform launched in 2025 and runs entirely in the cloud. It works on mobile and desktop, needs no installation, and has a{" "}
                <Link href="/free-trial" className="text-primary hover:underline font-medium">
                  14-day free trial
                </Link>{" "}
                with no credit card required. Most builders produce their first{" "}
                <Link href="/ai-quoting-software/" className="text-primary hover:underline font-medium">
                  AI-generated quote
                </Link>{" "}
                in under 10 minutes.
              </p>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">How it works</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Upload what you have. The AI builds the quote.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {HOW_IT_WORKS.map((item) => (
                <div key={item.step} className="bg-background rounded-xl border border-border p-6">
                  <div className="text-4xl font-bold text-primary/20 mb-3">{item.step}</div>
                  <h3 className="font-semibold text-base mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {item.description}
                  </p>
                  <Link
                    href={item.href}
                    className="text-sm text-primary font-medium hover:underline flex items-center gap-1"
                  >
                    <ChevronRight className="h-3 w-3" />
                    {item.cta}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">What drives the product</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Four principles behind every decision we make.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {VALUES.map((value) => (
                <div key={value.title} className="bg-muted/30 rounded-xl border border-border p-6">
                  <value.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10">Common questions</h2>
            <div className="space-y-6">
              {FAQ_SCHEMA.mainEntity.map((faq) => (
                <div key={faq.name} className="border-b border-border/40 pb-6 last:border-0">
                  <h3 className="font-semibold text-base mb-2">{faq.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.acceptedAnswer.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try it free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              5 total quote creations across Auto, Template, and Manual quotes included. No credit card required. Start quoting in under 10 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link href="/free-trial">
                  Start your free trial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/pricing/">See pricing</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              No credit card required · Cancel any time
            </p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
